import loadable from "@loadable/component"
import React from "react"
import DatoHighlightToExternalUrl from "./blocks/DatoHighlightToExternalUrl"
import type { DatoImageGalleryProps } from "./blocks/DatoImageGallery"
import DatoKlaviyoFormEmbed from "./blocks/DatoKlaviyoFormEmbed"
import type { DatoProductFilterProps } from "./blocks/DatoProductFilter"

const DatoApplicationDownload = loadable(() => import("./blocks/DatoApplicationDownload"))
const DatoArticleFinder = loadable(() => import("./blocks/DatoArticleFinder"))
const DatoBrochureList = loadable(() => import("./blocks/DatoBrochureList"))
const DatoContactCollectionRef = loadable(() => import("./blocks/DatoContactCollectionRef"))
const DatoContactLocationBlock = loadable(() => import("./blocks/DatoContactLocationBlock"))
const DatoContactPersons = loadable(() => import("./blocks/DatoContactPersons"))
const DatoCtaRef = loadable(() => import("./blocks/DatoCtaRef"))
const DatoCtaToArticle = loadable(() => import("./blocks/DatoCtaToArticle"))
const DatoCtaToExternalUrl = loadable(() => import("./blocks/DatoCtaToExternalUrl"))
const DatoCtaToPage = loadable(() => import("./blocks/DatoCtaToPage"))
const DatoCtaToProduct = loadable(() => import("./blocks/DatoCtaToProduct"))
const DatoCtaToProductFinder = loadable(() => import("./blocks/DatoCtaToProductFinder"))
const DatoDealerFinder = loadable(() => import("./blocks/DatoDealerFinder"))
const DatoExternalVideo = loadable(() => import("./blocks/DatoExternalVideo"))
const DatoFAQ = loadable(() => import("./blocks/DatoFaq"))
const DatoForm = loadable(() => import("./blocks/DatoForm"))
const DatoHighlightToArticle = loadable(() => import("./blocks/DatoHighlightToArticle"))
const DatoHighlightToPage = loadable(() => import("./blocks/DatoHighlightToPage"))
const DatoHighlightToProduct = loadable(() => import("./blocks/DatoHighlightToProduct"))
const DatoHighlightToProductFinder = loadable(() => import("./blocks/DatoHighlightToProductFinder"))
const DatoIframeBlock = loadable(() => import("./blocks/DatoIframeBlock"))
const DatoImageGallery = loadable(() => import("./blocks/DatoImageGallery"))
const DatoLatestArticles = loadable(() => import("./blocks/DatoLatestArticles"))
const DatoMediaBlock = loadable(() => import("./blocks/DatoMediaBlock"))
const DatoMediaWithText = loadable(() => import("./blocks/DatoMediaWithText"))
const DatoModularContentRef = loadable(() => import("./blocks/DatoModularContentRef"))
const DatoNumberFacts = loadable(() => import("./blocks/DatoNumberFacts"))
const DatoPickedArticles = loadable(() => import("./blocks/DatoPickedArticles"))
const DatoProductFilter = loadable(() => import("./blocks/DatoProductFilter"))
const DatoProductFinderLinkBar = loadable(() => import("./blocks/DatoProductFinderLinkBar"))
const DatoProductFinderTabs = loadable(() => import("./blocks/DatoProductFinderTabs"))
const DatoSaunaCalculatorBlock = loadable(() => import("./blocks/DatoSaunaCalculatorBlock"))
const DatoSaunaPlannerBlock = loadable(() => import("./blocks/DatoSaunaPlannerBlock"))
const DatoTabularContent = loadable(() => import("./blocks/DatoTabularContent"))
const DatoTextBlock = loadable(() => import("./blocks/DatoTextBlock"))
const DatoTileGrid = loadable(() => import("./blocks/DatoTileGrid"))
const DatoVideoWithText = loadable(() => import("./blocks/DatoVideoWithText"))
const DatoTypeform = loadable(() => import("./blocks/DatoTypeform"))
const DatoProductFilesList = loadable(() => import("./blocks/DatoProductFilesList"))
const DatoLeadooDynamicEmbedding = loadable(() => import("./blocks/DatoLeadooDynamicEmbedding"))

export type DatoBlockProps =
  | ({ __typename: "DatoCmsApplicationDownload" } & Queries.DatoApplicationDownloadFragment)
  | ({ __typename: "DatoCmsArticleFinder" } & Queries.DatoArticleFinderFragment)
  | ({ __typename: "DatoCmsBrochureList" } & Queries.DatoBrochureListBlockFragment)
  | ({ __typename: "DatoCmsContactCollectionRef" } & Queries.DatoContactCollectionRefFragment)
  | ({ __typename: "DatoCmsContactLocationBlock" } & Queries.DatoContactLocationBlockFragment)
  | ({ __typename: "DatoCmsContactPersonSet" } & Queries.DatoContactPersonsFragment)
  | ({ __typename: "DatoCmsCtaToArticle" } & Queries.DatoCtaToArticleFragment)
  | ({ __typename: "DatoCmsCtaToExternalUrl" } & Queries.DatoCtaToExternalUrlFragment)
  | ({ __typename: "DatoCmsCtaToPage" } & Queries.DatoCtaToPageFragment)
  | ({ __typename: "DatoCmsCtaRef" } & Queries.DatoCtaRefFragment)
  | ({ __typename: "DatoCmsCtaToProduct" } & Queries.DatoCtaToProductFragment)
  | ({ __typename: "DatoCmsCtaToProductFinder" } & Queries.DatoCtaToProductFinderFragment)
  | ({ __typename: "DatoCmsDealerFinder" } & Queries.DatoDealerFinderFragment)
  | ({ __typename: "DatoCmsExternalVideo" } & Queries.DatoExternalVideoFragment)
  | ({ __typename: "DatoCmsHighlightToPage" } & Queries.DatoHighlightToPageFragment)
  | ({ __typename: "DatoCmsIframeBlock" } & Queries.DatoIframeBlockFragment)
  | ({ __typename: "DatoCmsImageGallery" } & DatoImageGalleryProps)
  | ({ __typename: "DatoCmsLatestArticleSet" } & Queries.DatoLatestArticlesFragment)
  | ({ __typename: "DatoCmsHighlightToArticle" } & Queries.DatoHighlightToArticleFragment)
  | ({ __typename: "DatoCmsHighlightToProduct" } & Queries.DatoHighlightToProductFragment)
  | ({ __typename: "DatoCmsHighlightToProductFinder" } & Queries.DatoHighlightToProductFinderFragment)
  | ({ __typename: "DatoCmsHighlightToExternalUrl" } & Queries.DatoHighlightToExternalUrlFragment)
  | ({ __typename: "DatoCmsMediaBlock" } & Queries.DatoMediaBlockFragment)
  | ({ __typename: "DatoCmsMediaWithText" } & Queries.DatoMediaWithTextFragment)
  | ({ __typename: "DatoCmsModularContentRef" } & Queries.DatoModularContentRefFragment)
  | ({ __typename: "DatoCmsNumberFactSet" } & Queries.DatoNumberFactsFragment)
  | ({ __typename: "DatoCmsPickedArticleSet" } & Queries.DatoPickedArticlesFragment)
  | ({ __typename: "DatoCmsProductFilter" } & DatoProductFilterProps)
  | ({ __typename: "DatoCmsProductFinderTabSet" } & Queries.DatoProductFinderTabsFragment)
  | ({ __typename: "DatoCmsProductFinderLinkBar" } & Queries.DatoProductFinderLinkBarFragment)
  | ({ __typename: "DatoCmsSaunaCalculatorBlock" } & Queries.DatoSaunaCalculatorBlockFragment)
  | ({ __typename: "DatoCmsSaunaPlannerBlock" } & Queries.DatoSaunaPlannerBlockFragment)
  | ({ __typename: "DatoCmsTabularContent" } & Queries.DatoTabularContentFragment)
  | ({ __typename: "DatoCmsTextBlock" } & Queries.DatoTextBlockFragment)
  | ({ __typename: "DatoCmsTileGrid" } & Queries.DatoTileGridFragment)
  | ({ __typename: "DatoCmsVideoWithText" } & Queries.DatoVideoWithTextFragment)
  | ({ __typename: "DatoCmsForm" } & Queries.DatoFormFragment)
  | ({ __typename: "DatoCmsFaq" } & Queries.DatoFAQFragment)
  | ({ __typename: "DatoCmsTypeform" } & Queries.DatoTypeformFragment)
  | ({ __typename: "DatoCmsProductFilesList" } & Queries.DatoProductFilesListFragment)
  | ({ __typename: "DatoCmsKlaviyoFormEmbed" } & Queries.DatoKlaviyoFormEmbedFragment)
  | ({ __typename: "DatoCmsLeadooDynamicEmbedding" } & Queries.DatoLeadooDynamicEmbeddingFragment)

/**
 * Mappaa Datosta block-tyyppiset sisällöt React komponetteihin.
 */
const DatoBlock = (props: DatoBlockProps) => {
  // console.debug("Rendering DatoBlock", props)
  switch (props.__typename) {
    case "DatoCmsApplicationDownload":
      return <DatoApplicationDownload {...props} />
    case "DatoCmsArticleFinder":
      return <DatoArticleFinder {...props} />
    case "DatoCmsBrochureList":
      return <DatoBrochureList {...props} />
    case "DatoCmsContactCollectionRef":
      return <DatoContactCollectionRef {...props} />
    case "DatoCmsContactLocationBlock":
      return <DatoContactLocationBlock {...props} />
    case "DatoCmsContactPersonSet":
      return <DatoContactPersons {...props} />
    case "DatoCmsCtaToArticle":
      return <DatoCtaToArticle {...props} />
    case "DatoCmsCtaToExternalUrl":
      return <DatoCtaToExternalUrl {...props} />
    case "DatoCmsCtaToPage":
      return <DatoCtaToPage {...props} />
    case "DatoCmsCtaRef":
      return <DatoCtaRef {...props} />
    case "DatoCmsCtaToProduct":
      return <DatoCtaToProduct {...props} />
    case "DatoCmsCtaToProductFinder":
      return <DatoCtaToProductFinder {...props} />
    case "DatoCmsDealerFinder":
      return <DatoDealerFinder {...props} />
    case "DatoCmsExternalVideo":
      return <DatoExternalVideo {...props} />
    case "DatoCmsHighlightToPage":
      return <DatoHighlightToPage {...props} />
    case "DatoCmsIframeBlock":
      return <DatoIframeBlock {...props} />
    case "DatoCmsImageGallery":
      return <DatoImageGallery {...props} />
    case "DatoCmsLatestArticleSet":
      return <DatoLatestArticles {...props} />
    case "DatoCmsHighlightToArticle":
      return <DatoHighlightToArticle {...props} />
    case "DatoCmsHighlightToProduct":
      return <DatoHighlightToProduct {...props} />
    case "DatoCmsHighlightToProductFinder":
      return <DatoHighlightToProductFinder {...props} />
    case "DatoCmsHighlightToExternalUrl":
      return <DatoHighlightToExternalUrl {...props} />
    case "DatoCmsMediaBlock":
      return <DatoMediaBlock {...props} />
    case "DatoCmsMediaWithText":
      return <DatoMediaWithText {...props} />
    case "DatoCmsModularContentRef":
      return <DatoModularContentRef {...props} />
    case "DatoCmsNumberFactSet":
      return <DatoNumberFacts {...props} />
    case "DatoCmsPickedArticleSet":
      return <DatoPickedArticles {...props} />
    case "DatoCmsProductFilter":
      return <DatoProductFilter {...props} />
    case "DatoCmsProductFinderTabSet":
      return <DatoProductFinderTabs {...props} />
    case "DatoCmsProductFinderLinkBar":
      return <DatoProductFinderLinkBar {...props} />
    case "DatoCmsSaunaCalculatorBlock":
      return <DatoSaunaCalculatorBlock {...props} />
    case "DatoCmsSaunaPlannerBlock":
      return <DatoSaunaPlannerBlock {...props} />
    case "DatoCmsTabularContent":
      return <DatoTabularContent {...props} />
    case "DatoCmsTextBlock":
      return <DatoTextBlock {...props} />
    case "DatoCmsTileGrid":
      return <DatoTileGrid {...props} />
    case "DatoCmsVideoWithText":
      return <DatoVideoWithText {...props} />
    case "DatoCmsForm":
      return <DatoForm {...props} />
    case "DatoCmsFaq":
      return <DatoFAQ {...props} />
    case "DatoCmsTypeform":
      return <DatoTypeform {...props} />
    case "DatoCmsProductFilesList":
      return <DatoProductFilesList {...props} />
    case "DatoCmsKlaviyoFormEmbed":
      return <DatoKlaviyoFormEmbed {...props} />
    case "DatoCmsLeadooDynamicEmbedding":
      return <DatoLeadooDynamicEmbedding {...props} />
    default:
      console.warn("Unmapped module:", (props as any).__typename)
      return null
  }
}

export default DatoBlock
